import { ArtPurchasingHistory } from 'components/onboarding/steps/ArtPurchasingHistory'
import { FollowArtists } from 'components/onboarding/steps/FollowArtists'
import { PlansOnFairart } from 'components/onboarding/steps/PlansOnFairart'
import { Welcome } from 'components/onboarding/steps/Welcome'
import {
  ONBOARDING_STATUS,
  OnboardingStep,
  OnboardingSteps,
} from 'constants/onboarding'
import routes from 'constants/routes'
import { useAuth } from 'contexts'
import { useOverlay } from 'contexts/stores'
import dayjs from 'dayjs'
import { useUpdateOnboarding } from 'hooks/onboarding/useUpdateOnboarding'
import { useRouter } from 'next/router'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

const StepToContentMap = {
  [OnboardingSteps.welcome]: <Welcome />,
  [OnboardingSteps.artPurchasingHistory]: <ArtPurchasingHistory />,
  [OnboardingSteps.followArtists]: <FollowArtists />,
  [OnboardingSteps.plansOnFairart]: <PlansOnFairart />,
} as const

interface State {
  step: OnboardingStep | null
  display: VoidFunction
  next: VoidFunction
  prev: VoidFunction
  close: VoidFunction
  skip: VoidFunction
  start: VoidFunction
  finish: VoidFunction
}

export const OnboardingContext = createContext<State>({} as State)

export function OnboardingProvider({ children }: PropsWithChildren) {
  const router = useRouter()
  const { setIsOpen, setConfig } = useOverlay()
  // const { data: userProfile } = useUserProfile()
  const { mutateAsync: updateOnboarding } = useUpdateOnboarding()
  const { user } = useAuth()
  const [step, setStep] = useState<OnboardingStep | null>(null)
  const redirectAfterSocialSignup = useRef<string | null>(null)

  useEffect(() => {
    redirectAfterSocialSignup.current = localStorage.getItem('redirectTo')
  }, [])

  useEffect(() => {
    if (!step) return
    const content = StepToContentMap[step]
    if (!content) return

    setConfig({
      body: content,
      className: 'h-screen',
      onClose: () => updateOnboarding({ status: ONBOARDING_STATUS.SKIPPED }),
    })
    setIsOpen(true)
  }, [step])

  // will be needed in the future
  // useEffect(() => {
  //   function determineStep() {
  //     if (
  //       !userProfile?.onboarding?.status ||
  //       userProfile?.onboarding?.status !== ONBOARDING_STATUS.NOT_SHOWN
  //     ) {
  //       setStep(null)
  //       return
  //     }

  //     setStep(OnboardingSteps.welcome)
  //   }

  //   determineStep()
  // }, [userProfile?.onboarding?.status])

  // will be needed in the future
  // useEffect(() => {
  //   function displayOnboarding() {
  //     if (
  //       router.asPath.startsWith(routes.askOrSell.base) ||
  //       router.asPath.startsWith(routes.bidOrBuy.base) ||
  //       !step
  //     ) {
  //       return
  //     }

  //     const content = StepToContentMap[step]
  //     if (!content) return

  //     setConfig({
  //       body: content,
  //       className: 'h-screen',
  //     })
  //     setIsOpen(true)
  //   }

  //   displayOnboarding()
  // }, [step, router.asPath])

  const display = useCallback(() => {
    if (
      router.asPath.startsWith(routes.askOrSell.base) ||
      router.asPath.startsWith(routes.bidOrBuy.base) ||
      router.asPath.startsWith(routes.auctionPlaceBid.reviewBid) ||
      router.asPath.startsWith(routes.auctionPlaceBid.shippingAndBilling)
    ) {
      return
    }

    setStep(OnboardingSteps.welcome)
  }, [router.asPath])

  const start = useCallback(() => {
    return updateOnboarding({ status: ONBOARDING_STATUS.INCOMPLETE })
  }, [updateOnboarding])

  const next = useCallback(() => {
    setStep(prev => {
      switch (prev) {
        case OnboardingSteps.welcome:
          return OnboardingSteps.followArtists
        case OnboardingSteps.followArtists:
          return OnboardingSteps.artPurchasingHistory
        case OnboardingSteps.artPurchasingHistory:
          return OnboardingSteps.plansOnFairart
      }
      return prev
    })
  }, [])

  const prev = useCallback(() => {
    setStep(prev => {
      switch (prev) {
        case OnboardingSteps.followArtists:
          return OnboardingSteps.welcome
        case OnboardingSteps.artPurchasingHistory:
          return OnboardingSteps.followArtists
        case OnboardingSteps.plansOnFairart:
          return OnboardingSteps.artPurchasingHistory
      }
      return prev
    })
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
  }, [])

  const skip = useCallback(() => {
    return updateOnboarding(
      { status: ONBOARDING_STATUS.SKIPPED },
      { onSuccess: close }
    )
  }, [close, updateOnboarding])

  const finish = useCallback(() => {
    return updateOnboarding(
      { status: ONBOARDING_STATUS.COMPLETED },
      { onSuccess: close }
    )
  }, [close, updateOnboarding])

  // redirect the user after social sign up
  useEffect(() => {
    if (
      redirectAfterSocialSignup?.current?.startsWith(routes.askOrSell.base) ||
      redirectAfterSocialSignup?.current?.startsWith(routes.bidOrBuy.base) ||
      (user?.provider !== 'google' && user?.provider !== 'apple')
    )
      return

    const currentDate = dayjs()
    const userCreatedDate = dayjs(user.createdAt)
    const minutesDiff = currentDate.diff(userCreatedDate, 'minutes')
    if (minutesDiff > 1) return

    display()
  }, [user, display])

  return (
    <OnboardingContext.Provider
      value={{ step, next, prev, close, skip, display, finish, start }}
    >
      {children}
    </OnboardingContext.Provider>
  )
}
